import React, { useMemo, useState } from 'react';
import { ClockIcon, QueueListIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useTradeActivity } from '@/hooks/trades';
import type { PositionDetail } from '@/types/trades.types';
import type { ActivityEvent } from '@/types';
import { groupActivityByDay, orderActivityForDisplay } from '@/features/trades/utils/activity-grouping';
import { getEffectiveTradeStatusLabel } from '@/utils/trades/effective-status';
import { TabState } from '../TabState';
import { ActivityRow } from '../activity/ActivityRow';
import {
  ActivityFilter,
  type ActivityFilterValue,
} from '../activity/ActivityFilter';

interface ActivityTabProps {
  tradeId: number;
  trade: PositionDetail;
}

/** Client-side page size; the pager only appears when the filtered stream exceeds it. */
const PAGE_SIZE = 30;

/**
 * The one converged Activity / History tab — the ADR-0001 "Trade movement
 * timeline" projection for a single trade. Renders the normalized
 * `useTradeActivity` stream through the Phase-8 <TabState> seam: a day-grouped
 * vertical rail of <ActivityRow>, with a header family filter. Empty is reached
 * ONLY from a resolved zero-row query; no fabricated rows in any branch; no
 * DataSourceBadge; timestamps via formatDate/formatDateTime (never split('T')).
 */
export function ActivityTab({ tradeId, trade }: ActivityTabProps) {
  const { data, isLoading, isError, refetch } = useTradeActivity(tradeId);
  const [filter, setFilter] = useState<ActivityFilterValue>('ALL');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);

  const events = useMemo(() => data ?? [], [data]);

  const filteredEvents = useMemo(
    () => (filter === 'ALL' ? events : events.filter((e) => e.family === filter)),
    [events, filter],
  );

  // The /audit/ endpoint returns rows ASCENDING by occurred_at (oldest-first), verbatim.
  // Order the WHOLE filtered stream into DISPLAY order BEFORE paginating, using the SAME
  // contract as the day grouping (newest-first; within a day timestamped-desc then
  // `approximate` rows sunk to the end). A raw occurred_at sort placed date-only approximate
  // rows at their midnight position, disagreeing with where groupActivityByDay sinks them →
  // an approximate row could be paginated onto the wrong page (M6, and CR-01). Pure copy —
  // never mutate the React Query cache array (WR-04).
  const orderedEvents = useMemo(() => orderActivityForDisplay(filteredEvents), [filteredEvents]);

  // Stable, collision-free row key per event (M5): a real transaction_id / mtm_id when the
  // row has one, else the event identity + its GLOBAL ordered index. The old
  // `${occurred_at}-${dayGroupIndex}` collided for same-timestamp rows across day groups
  // (esp. date-only `T00:00:00` approximate rows), corrupting the expand Set + ARIA ids.
  const rowKeyOf = useMemo(() => {
    const keys = new Map<ActivityEvent, string>();
    orderedEvents.forEach((e, idx) => {
      const key =
        e.links.transaction_id != null
          ? `txn:${e.links.transaction_id}`
          : e.links.mtm_id != null
            ? `mtm:${e.links.mtm_id}`
            : `evt:${e.event_type}:${e.occurred_at}:${idx}`;
      keys.set(e, key);
    });
    return keys;
  }, [orderedEvents]);

  // Client-side pagination over the ordered stream; day grouping applies within the page.
  const totalPages = Math.ceil(orderedEvents.length / PAGE_SIZE);
  const pageEvents = useMemo(() => {
    const start = page * PAGE_SIZE;
    return orderedEvents.slice(start, start + PAGE_SIZE);
  }, [orderedEvents, page]);

  const dayGroups = useMemo(() => groupActivityByDay(pageEvents), [pageEvents]);

  const isEmpty = !isLoading && !isError && events.length === 0;

  const toggleRow = (rowId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  const handleFilterChange = (value: ActivityFilterValue) => {
    setFilter(value);
    setPage(0);
  };

  // Current-state summary line — derived honestly from the trade (no fabrication).
  const settledNote =
    trade.cash_settled && trade.securities_settled
      ? 'fully settled'
      : trade.cash_settled || trade.securities_settled
        ? 'partially settled'
        : 'awaiting settlement';
  // LR-497: same derived effective label as the badge/header pill, so the
  // state line never disagrees with what the rest of the page shows.
  const stateLine = `${getEffectiveTradeStatusLabel(trade)} · ${trade.quantity.toLocaleString()} ${trade.symbol_id} · ${settledNote}`;

  return (
    <div className="mx-auto max-w-screen-2xl space-y-6">
      <TabState
        isLoading={isLoading}
        isError={isError}
        isEmpty={isEmpty}
        onRetry={() => refetch()}
        loadingLabel="Loading activity"
        errorSection="activity"
        empty={{
          icon: QueueListIcon,
          heading: 'No activity recorded yet',
          body: 'This trade has no recorded transactions, marks, or lifecycle events yet. Activity will appear here as it occurs.',
        }}
      >
        <div className="rounded-[10px] border border-lr-border bg-lr-surface p-6">
          {/* Current-state summary */}
          <div className="mb-5 flex items-center gap-3 rounded-[10px] border border-lr-border bg-lr-surface-2 p-3.5">
            <div className="grid h-9 w-9 flex-none place-items-center rounded-lg bg-lr-pos-weak text-lr-pos">
              <CheckCircleIcon className="h-5 w-5" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-[0.5px] text-lr-muted">
                Current state
              </div>
              <div className="mt-0.5 text-[15px] font-bold text-lr-fg">{stateLine}</div>
            </div>
          </div>

          {/* Header */}
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-lr-fg">
              <ClockIcon className="h-4 w-4 text-lr-muted" aria-hidden="true" />
              ACTIVITY / HISTORY
            </h3>
            <div className="flex flex-wrap items-center gap-3">
              <ActivityFilter events={events} value={filter} onChange={handleFilterChange} />
              <span className="text-xs text-lr-faint">{filteredEvents.length} events</span>
            </div>
          </div>

          {filteredEvents.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-400">
              No{' '}
              {filter === 'ALL'
                ? 'matching'
                : filter.charAt(0).toUpperCase() + filter.slice(1)}{' '}
              events
            </p>
          ) : (
            <div className="relative">
              {/* Continuous vertical rail behind the dots */}
              <div
                className="absolute left-3 top-2 bottom-2 w-px bg-gray-200"
                aria-hidden="true"
              />

              <div className="space-y-6">
                {dayGroups.map((group) => (
                  <div key={group.dayKey} className="space-y-6">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {group.dayLabel} · {group.events.length}{' '}
                      {group.events.length === 1 ? 'event' : 'events'}
                    </h4>
                    {group.events.map((event, i) => {
                      const rowId = rowKeyOf.get(event) ?? `${event.occurred_at}-${i}`;
                      return (
                        <ActivityRow
                          key={rowId}
                          event={event}
                          rowId={rowId}
                          isExpanded={expanded.has(rowId)}
                          onToggle={() => toggleRow(rowId)}
                          trade={trade}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>

              {/* Pager — only when the filtered stream exceeds one page */}
              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-4">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="rounded bg-gray-100 px-3 py-1 text-sm text-gray-600 hover:bg-gray-200 disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <span className="text-xs text-gray-500">
                    Page {page + 1} of {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    className="rounded bg-gray-100 px-3 py-1 text-sm text-gray-600 hover:bg-gray-200 disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </TabState>
    </div>
  );
}
